import { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TablePagination,
  TextField,
  InputAdornment,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useGetRefundsAnalyticsQuery, useDeleteRefundsLeadMutation } from "@/services";

function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

function formatDate(iso: string): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const FUNNEL_ORDER = ["signup", "code_sent", "verified", "results"];
const FUNNEL_LABELS: Record<string, string> = {
  signup: "Signed Up",
  code_sent: "Code Sent",
  verified: "Verified",
  results: "Got Results",
};

const STEP_COLORS: Record<string, "default" | "warning" | "success" | "info"> = {
  signup: "default",
  code_sent: "warning",
  verified: "info",
  results: "success",
};

export default function RefundsAnalyticsPage() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const { data, isLoading, isFetching } = useGetRefundsAnalyticsQuery(
    {
      pageNo: page + 1,
      pageSize: rowsPerPage,
      searchKeyword: search,
    },
    { refetchOnMountOrArgChange: true }
  );

  const [deleteLead] = useDeleteRefundsLeadMutation();

  const handleDelete = async (email: string) => {
    if (!window.confirm(`Delete lead ${email} and all associated data?`)) return;
    try {
      const res = await deleteLead({ email }).unwrap();
      if (!res.deleteRefundsLead.success) {
        alert(res.deleteRefundsLead.error || "Failed to delete");
      }
    } catch {
      alert("Failed to delete lead");
    }
  };

  const dashboard = data?.refundsAnalytics?.data;
  const summary = dashboard?.summary;
  const leads = dashboard?.leads ?? [];
  const meta = dashboard?.leadsMeta;
  const funnel = dashboard?.funnel ?? [];
  const eventCounts = dashboard?.eventCounts ?? [];
  const topCompanies = dashboard?.topCompanies ?? [];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={3}>
        Refunds Tool Analytics
      </Typography>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Summary cards */}
          <Grid container spacing={2} mb={3}>
            {[
              { label: "Total Leads", value: summary?.totalLeads ?? 0 },
              { label: "Verified", value: summary?.verifiedLeads ?? 0 },
              { label: "Got Results", value: summary?.leadsWithAnalysis ?? 0 },
              { label: "Total Uploads", value: summary?.totalUploadCount ?? 0 },
              {
                label: "Total Refund Identified",
                value: formatCurrency(summary?.totalRefundIdentified ?? 0),
              },
              {
                label: "Avg Refund / Lead",
                value: formatCurrency(summary?.averageRefundPerLead ?? 0),
              },
              {
                label: "With Liquidation Data",
                value: summary?.leadsWithLiquidationData ?? 0,
              },
            ].map((item) => (
              <Grid item xs={6} sm={4} md={12 / 7} key={item.label}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                    <Typography variant="caption" color="text.secondary">
                      {item.label}
                    </Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {item.value}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Funnel + Events + Top Companies */}
          <Grid container spacing={2} mb={3}>
            {/* Funnel */}
            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="subtitle2" fontWeight={700} mb={2}>
                    Funnel
                  </Typography>
                  {FUNNEL_ORDER.map((step) => {
                    const entry = funnel.find((f) => f.step === step);
                    return (
                      <Box
                        key={step}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        py={0.5}
                      >
                        <Typography variant="body2">
                          {FUNNEL_LABELS[step] ?? step}
                        </Typography>
                        <Chip
                          label={entry?.count ?? 0}
                          size="small"
                          color={STEP_COLORS[step] ?? "default"}
                        />
                      </Box>
                    );
                  })}
                </CardContent>
              </Card>
            </Grid>

            {/* Events */}
            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="subtitle2" fontWeight={700} mb={2}>
                    Event Counts
                  </Typography>
                  {eventCounts.slice(0, 10).map((ev) => (
                    <Box
                      key={ev.event}
                      display="flex"
                      justifyContent="space-between"
                      py={0.5}
                    >
                      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                        {ev.event}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {ev.count.toLocaleString()}
                      </Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>

            {/* Top Companies */}
            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="subtitle2" fontWeight={700} mb={2}>
                    Top Companies by Refund
                  </Typography>
                  {topCompanies.map((c) => (
                    <Box
                      key={c.company}
                      display="flex"
                      justifyContent="space-between"
                      py={0.5}
                    >
                      <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                        {c.company}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(c.totalRefund)}
                      </Typography>
                    </Box>
                  ))}
                  {topCompanies.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No data yet
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Leads table */}
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle2" fontWeight={700}>
                  Leads ({meta?.total ?? 0})
                </Typography>
                <form onSubmit={handleSearch}>
                  <TextField
                    size="small"
                    placeholder="Search email, name, company..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ width: 300 }}
                  />
                </form>
              </Box>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Company</TableCell>
                      <TableCell>Step</TableCell>
                      <TableCell align="right">Refund</TableCell>
                      <TableCell align="right">Entries</TableCell>
                      <TableCell align="right">Uploads</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell align="center" sx={{ width: 48 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {leads.map((lead) => (
                      <TableRow key={lead.email} hover>
                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                          {lead.email}
                        </TableCell>
                        <TableCell>
                          {lead.firstName} {lead.lastName}
                        </TableCell>
                        <TableCell>{lead.company}</TableCell>
                        <TableCell>
                          <Chip
                            label={FUNNEL_LABELS[lead.funnelStep] ?? lead.funnelStep}
                            size="small"
                            color={STEP_COLORS[lead.funnelStep] ?? "default"}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {lead.latestTotalRefund != null
                            ? formatCurrency(lead.latestTotalRefund)
                            : "—"}
                        </TableCell>
                        <TableCell align="right">
                          {lead.latestEntryCount?.toLocaleString() ?? "—"}
                        </TableCell>
                        <TableCell align="right">{lead.uploadCount}</TableCell>
                        <TableCell>
                          {lead.utmSource || lead.source || "—"}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>
                          {formatDate(lead.createdAt)}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Delete lead">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(lead.email)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {leads.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            {isFetching ? "Loading..." : "No leads yet"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={meta?.total ?? 0}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}
